// C:\MonProjet\BBW_DELEGUER\preprod\babyworld-backoffice\babyworld-backoffice\src\components\DoctorHistoryModal.js

import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { 
  Modal, Spin, Row, Col, Typography, Tag, Space, 
  Collapse, Badge, List, Button, Tooltip, Empty, message 
} from "antd";
import { 
  AuditOutlined, DownloadOutlined, StarFilled, WarningOutlined, 
  RocketOutlined, UserOutlined, StopOutlined, EnvironmentOutlined, 
  CheckCircleOutlined, CrownOutlined 
} from "@ant-design/icons";
import axiosInstance from "../services/axiosServicesVisit";
import dayjs from "dayjs";

const { Text, Title } = Typography;

// --- CONFIGURATION EXACTE DE DOCTORS PAGE ---
export const RELATIONSHIP_CONFIG = {
  PARTNER_ACTIVE: {
    label: "⭐ Partenaire Stratégique [B+]",
    tagColor: "gold",
    icon: <StarFilled />,
    bgColor: "#fffbe6",
    borderColor: "#fadb14",
    titleColor: "#856404",
    shadow: "0 4px 12px rgba(250, 219, 20, 0.18)"
  },
  PARTNER_AT_RISK: {
    label: "⚠️ Partenaire Sous Vigilance [A-]",
    tagColor: "warning",
    icon: <WarningOutlined />,
    bgColor: "#fff7e6",
    borderColor: "#fa8c16",
    titleColor: "#d46b08",
    shadow: "0 4px 12px rgba(250, 140, 22, 0.18)"
  },
  KEY_PRESCRIBER: {
    label: "🚀 Prescripteur [B]",
    tagColor: "success",
    icon: <RocketOutlined />,
    bgColor: "#f6ffed",
    borderColor: "#52c41a",
    titleColor: "#237804",
    shadow: "0 4px 12px rgba(82, 196, 26, 0.18)"
  },
  STANDARD: {
    label: "👤 Standard [C]",
    tagColor: "default",
    icon: <UserOutlined />,
    bgColor: "#f0f5ff",
    borderColor: "#adc6ff",
    titleColor: "#002766",
    shadow: "none"
  },
  REFRACTORY: {
    label: "⛔ Non Réceptif [C-]",
    tagColor: "error",
    icon: <StopOutlined />,
    bgColor: "#fff1f0",
    borderColor: "#ff4d4f",
    titleColor: "#cf1322",
    shadow: "0 4px 12px rgba(255, 77, 79, 0.18)"
  }
};

const getCategoryTagColor = (cat) => {
  if (!cat) return 'default';
  const c = String(cat).toUpperCase();
  if (c === 'AKTIV') return 'blue';
  if (c === 'SANOSAN') return 'green';
  if (c === 'MATERNITE') return 'purple';
  return 'cyan';
};

const DoctorHistoryModal = ({ 
  visible, 
  open, 
  onClose, 
  personId, 
  directPerson = null,
  delegatesList: propDelegatesList = null,
  zIndex = 2000 
}) => {
  const isModalOpen = visible !== undefined ? visible : open;

  const reduxDelegates = useSelector((state) => state.delegates?.delegatesList?.data?.data || []);
  const delegatesListData = propDelegatesList || reduxDelegates;

  const [doctorLoading, setDoctorLoading] = useState(false);
  const [doctorDetailData, setDoctorDetailData] = useState([]);

  useEffect(() => {
    if (!isModalOpen || !personId) {
      setDoctorDetailData([]);
      return;
    }

    let isMounted = true;
    setDoctorLoading(true);

    const loadData = async () => {
      try {
        const [resDetails, resPerson] = await Promise.all([
          axiosInstance.get(`/v1/delegate-stocks/analytics/doctor-full-details`, {
            params: { 
              personId, 
              startDate: '2020-01-01', 
              endDate: dayjs().format('YYYY-MM-DD'), 
              _t: Date.now() 
            }
          }),
          axiosInstance.get(`/v1/persons/${personId}`).catch(() => null)
        ]);

        if (!isMounted) return;

        const details = resDetails.data || [];
        const pData = resPerson?.data?.data || resPerson?.data || directPerson;

        if (details.length > 0) {
          if (pData) {
            details[0].relationshipCategory = pData.relationshipCategory || details[0].relationshipCategory;
            details[0].isPartner = (pData.isPartner !== undefined) ? pData.isPartner : details[0].isPartner;
            details[0].partnerNotes = pData.partnerNotes || details[0].partnerNotes;
            details[0].address = pData.address || details[0].address;
            if (pData.delegates) {
              details[0].assignedDelegates = pData.delegates.map(d => `${d.firstname || ''} ${d.lastname || ''}`.trim());
            }
          }
        } else if (pData) {
          details.push({
            doctorName: `Dr. ${pData.firstname || ''} ${pData.lastname || ''}`.trim(),
            specialtyName: pData.doctor?.specialty?.name || pData.specialty?.name || pData.specialtyName || "N/A",
            regionName: pData.region?.name || pData.regionName || "N/A",
            address: pData.address || "",
            isPartner: pData.isPartner,
            relationshipCategory: pData.relationshipCategory,
            assignedDelegates: pData.delegates?.map(d => `${d.firstname || ''} ${d.lastname || ''}`.trim()) || [],
            partnerNotes: pData.partnerNotes
          });
        }

        setDoctorDetailData(details);
      } catch (e) {
        message.error("Erreur lors du chargement de l'historique");
      } finally {
        if (isMounted) setDoctorLoading(false);
      }
    };

    loadData();

    return () => { isMounted = false; };
  }, [isModalOpen, personId, directPerson]);

  // Export CSV avec Label
  const handleExportDoctorHistory = () => {
    if (doctorDetailData.length === 0) return;
    const docName = doctorDetailData[0].doctorName;
    const headers = ["Date", "Region", "Delegue Visiteur", "Gamme", "Type Visite", "Echantillons", "Produits", "Observations / Note"];
    const rows = doctorDetailData.map(item => {
      const foundDelegate = delegatesListData.find(d => 
        (item.delegateId && d.id === item.delegateId) ||
        (item.delegateName && `${d.firstname} ${d.lastname}`.trim().toLowerCase() === item.delegateName.trim().toLowerCase())
      );
      const delCategory = item.category || item.delegateCategory || foundDelegate?.category || '-';

      const isSupervisor = (item.note && (item.note.includes('[RAPPORT SUPERVISEUR') || item.note.includes('[VISITE DE CONTRÔLE') || item.note.includes('[SUPERVISION'))) ||
                           (item.name && item.name.includes('[SUPERVISEUR]'));

      const isUnfinished = item.status === 'VISITE_NON_TERMINEE' || item.status === 'VISITE_NON_ABOUTIE' || (item.note && (item.note.includes('[VISITE NON TERMINÉE]') || item.note.includes('[VISITE NON ABOUTIE]')));
      const isSpecific = item.status === 'VISITE_SPECIFIQUE' || (item.note && item.note.includes('[VISITE SPÉCIFIQUE'));

      let typeLabel = "Délégué";
      if (isSupervisor) typeLabel = "Superviseur";
      else if (isUnfinished) typeLabel = "Visite Non Aboutie";
      else if (isSpecific) typeLabel = "Visite Spécifique";

      return [
        dayjs(item.reportDate).format('DD/MM/YYYY HH:mm'),
        item.regionName || "N/A",
        item.delegateName,
        delCategory,
        typeLabel,
        // ⚡ CSV : Affiche en priorité le label court, sinon le nom
        item.samples?.map(s => `${s.label || s.name} (x${s.quantity})`).join(' | ') || 'Aucun',
        item.products?.join(' | ') || '-',
        `"${(item.note || item.comment || '').replace(/"/g, '""')}"`
      ];
    });
    const csvContent = [headers, ...rows].map(e => e.join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Parcours_${docName.replace(/\s+/g, '_')}.csv`;
    link.click();
  };

  return (
    <Modal 
      title={ 
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '95%', alignItems: 'center' }}> 
          <span><AuditOutlined /> Parcours de visite du Médecin</span> 
          <Button 
            icon={<DownloadOutlined />} 
            size="small" 
            type="primary" 
            onClick={handleExportDoctorHistory} 
            disabled={doctorDetailData.length === 0}
          >
            Exporter Historique
          </Button> 
        </div> 
      } 
      open={isModalOpen}
      visible={isModalOpen}
      onCancel={onClose} 
      footer={null} 
      width={1150} 
      zIndex={zIndex}
      destroyOnClose
    >
      <Spin spinning={doctorLoading}>
        {doctorDetailData.length > 0 && (
          (() => {
            const firstRecord = doctorDetailData[0];
            const cat = firstRecord.relationshipCategory || (firstRecord.isPartner ? 'PARTNER_ACTIVE' : 'STANDARD');
            const catConfig = RELATIONSHIP_CONFIG[cat] || RELATIONSHIP_CONFIG.STANDARD;
            
            const cyclesCoverage = (firstRecord.cyclesPlanningCoverage || []).map(cov => ({
              ...cov,
              unassignedCycles: (cov.unassignedCycles || []).filter(c => {
                if (!c.startDate) return false;
                return dayjs(c.startDate).year() >= 2026;
              })
            }));

            const delegatesWithUnassigned = cyclesCoverage.filter(cov => (cov.unassignedCycles || []).length > 0);
            const totalOublisCount = delegatesWithUnassigned.reduce((acc, curr) => acc + (curr.unassignedCycles?.length || 0), 0);

            return (
              <div>
                {/* 1. ENCADRÉ AVEC COULEUR DYNAMIQUE SELON LE STATUT DU MÉDECIN */}
                <div style={{ 
                  marginBottom: 16, 
                  padding: '16px 20px', 
                  borderRadius: '10px', 
                  backgroundColor: catConfig.bgColor, 
                  border: `2px solid ${catConfig.borderColor}`,
                  boxShadow: catConfig.shadow
                }}>
                  <Row gutter={[16, 16]} align="middle">
                    <Col span={16}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
                          <span style={{ fontSize: '28px', marginTop: '5px', color: catConfig.titleColor }}>
                            {catConfig.icon}
                          </span>
                          <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                  <Title level={3} style={{ margin: 0, color: catConfig.titleColor }}>
                                      {firstRecord.doctorName}
                                  </Title>
                                  <Tag color={catConfig.tagColor} style={{ 
                                      fontSize: '12px', 
                                      fontWeight: 'bold', 
                                      padding: '2px 10px',
                                      borderRadius: '4px',
                                      border: `1px solid ${catConfig.borderColor}`
                                  }}>
                                      {catConfig.icon} {catConfig.label.toUpperCase()}
                                  </Tag>
                              </div>

                              <Space wrap size={8} style={{ marginTop: '8px' }}>
                                  <Tag color="blue" style={{ fontWeight: 'bold' }}>{firstRecord.specialtyName}</Tag>
                                  <Text type="secondary"><EnvironmentOutlined /> {firstRecord.regionName || "N/A"}</Text>
                                  {firstRecord.address && <Text type="secondary">({firstRecord.address})</Text>}
                              </Space>
                          </div>
                      </div>
                    </Col>

                    <Col span={8} style={{ 
                        textAlign: 'right', 
                        borderLeft: `2px solid ${catConfig.borderColor}`, 
                        paddingLeft: '20px' 
                    }}>
                      <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '8px' }}>
                        Délégués assignés au portefeuille :
                      </Text>
                      <Space wrap size={[4, 8]}>
                        {firstRecord.assignedDelegates?.length > 0 ? (
                          firstRecord.assignedDelegates.map((name, idx) => {
                            const delObj = delegatesListData.find(d => 
                              `${d.firstname} ${d.lastname}`.trim().toLowerCase() === (typeof name === 'string' ? name : '').trim().toLowerCase() ||
                              `${d.lastname} ${d.firstname}`.trim().toLowerCase() === (typeof name === 'string' ? name : '').trim().toLowerCase()
                            );
                            return (
                              <Tag key={idx} color="cyan" icon={<RocketOutlined />} style={{ borderRadius: '10px' }}>
                                {name.toUpperCase()} {delObj?.category ? `(${delObj.category})` : ''}
                              </Tag>
                            );
                          })
                        ) : (
                          <Text type="secondary" italic style={{ fontSize: '11px' }}>Aucun délégué assigné</Text>
                        )}
                      </Space>
                    </Col>
                  </Row>
                </div>

                {/* 2. ACCORDÉON DES OUBLIS DE PLANIFICATION */}
                {cyclesCoverage.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <Collapse
                      defaultActiveKey={delegatesWithUnassigned.length > 0 ? ['audit_oubli'] : []}
                      style={{ 
                        background: delegatesWithUnassigned.length > 0 ? '#fff2f0' : '#f6ffed', 
                        border: delegatesWithUnassigned.length > 0 ? '1px solid #ffa39e' : '1px solid #b7eb8f',
                        borderRadius: '8px'
                      }}
                      items={[
                        {
                          key: 'audit_oubli',
                          label: (
                            <Space>
                              {delegatesWithUnassigned.length > 0 ? (
                                <>
                                  <WarningOutlined style={{ color: '#cf1322', fontSize: '16px' }} />
                                  <span style={{ color: '#cf1322', fontWeight: 'bold', fontSize: '13px' }}>
                                    Alertes Oublis de Planification par Cycle
                                  </span>
                                  <Badge count={`${totalOublisCount} cycle(s) oublié(s)`} style={{ backgroundColor: '#cf1322' }} />
                                </>
                              ) : (
                                <>
                                  <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
                                  <span style={{ color: '#389e0d', fontWeight: 'bold', fontSize: '13px' }}>
                                    Planification Optimale : Ce médecin est planifié dans 100% des cycles
                                  </span>
                                  <Tag color="success" style={{ marginLeft: 8 }}>100% Couvert</Tag>
                                </>
                              )}
                            </Space>
                          ),
                          children: (
                            <div>
                              {delegatesWithUnassigned.length > 0 ? (
                                delegatesWithUnassigned.map((cov, idx) => {
                                  const unassignedList = cov.unassignedCycles || [];
                                  const assignedList = cov.assignedCycles || [];

                                  return (
                                    <div 
                                      key={idx} 
                                      style={{ 
                                        padding: '10px 14px', 
                                        background: '#ffffff', 
                                        borderRadius: '6px', 
                                        border: '1px solid #ffccc7',
                                        marginBottom: idx !== delegatesWithUnassigned.length - 1 ? 8 : 0 
                                      }}
                                    >
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                        <Text strong style={{ color: '#1890ff', fontSize: '13px' }}>
                                          👤 Délégué : {cov.delegateName}
                                        </Text>
                                        <Tag color="default" style={{ fontSize: '11px', color: '#595959' }}>
                                          Planifié dans {assignedList.length} autre(s) cycle(s)
                                        </Tag>
                                      </div>

                                      <div>
                                        <Text strong style={{ fontSize: '12px', color: '#cf1322', display: 'block', marginBottom: 6 }}>
                                          ⚠️ NON ASSIGNÉ dans ces cycles ({unassignedList.length}) :
                                        </Text>
                                        <Space wrap size={[6, 8]}>
                                          {unassignedList.map((c, cIdx) => {
                                            const periodStr = c.startDate && c.endDate 
                                              ? `(${dayjs(c.startDate).format('DD/MM/YY')} au ${dayjs(c.endDate).format('DD/MM/YY')})`
                                              : '';

                                            return (
                                              <Tag 
                                                key={cIdx} 
                                                color="error" 
                                                style={{ 
                                                  fontWeight: 'bold', 
                                                  fontSize: '11px', 
                                                  padding: '3px 10px',
                                                  borderRadius: '4px' 
                                                }}
                                              >
                                                ⚠️ {c.cycleName} <span style={{ fontWeight: 'normal', opacity: 0.9, marginLeft: 4 }}>{periodStr}</span>
                                              </Tag>
                                            );
                                          })}
                                        </Space>
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                <Text style={{ color: '#389e0d', fontSize: '12px' }}>
                                  Tous les délégués assignés ont bien planifié ce médecin dans l'ensemble de leurs cycles. Aucun oubli détecté.
                                </Text>
                              )}
                            </div>
                          )
                        }
                      ]}
                    />
                  </div>
                )}
              </div>
            );
          })()
        )}

        {/* 3. EN-TÊTE DU TABLEAU DES VISITES */}
        <div style={{ 
          background: '#f5f5f5', 
          padding: '10px 15px', 
          borderRadius: '6px', 
          marginBottom: '10px', 
          display: 'flex', 
          fontWeight: 'bold',
          fontSize: '12px',
          border: '1px solid #e8e8e8'
        }}>
          <div style={{ width: '15%' }}>Date & Heure</div>
          <div style={{ width: '20%' }}>Visiteur</div>
          <div style={{ width: '16%' }}>Échantillons distribués</div>
          <div style={{ width: '16%' }}>Produits présentés</div>
          <div style={{ width: '33%' }}>Observations / Notes</div>
        </div>

        {/* 4. LISTE DES VISITES */}
        <List 
          dataSource={doctorDetailData} 
          locale={{ emptyText: "Aucune visite trouvée pour ce médecin." }}
          renderItem={item => {
            const isSupervisor = item.isSupervisor || 
              (item.note && (item.note.includes('[RAPPORT SUPERVISEUR') || item.note.includes('[VISITE DE CONTRÔLE') || item.note.includes('[SUPERVISION'))) ||
              (item.name && (item.name.includes('[SUPERVISEUR]') || item.name.toLowerCase().includes('visite de contrôle')));

            const isUnfinished = item.status === 'VISITE_NON_TERMINEE' || 
              item.status === 'VISITE_NON_ABOUTIE' ||
              (item.note && (item.note.includes('[VISITE NON TERMINÉE]') || item.note.includes('[VISITE NON ABOUTIE]'))) ||
              (item.comment && (item.comment.includes('[VISITE NON TERMINÉE]') || item.comment.includes('[VISITE NON ABOUTIE]'))) ||
              (item.name && (item.name.toLowerCase().includes('non terminée') || item.name.toLowerCase().includes('non aboutie')));

            const isSpecificVisit = item.status === 'VISITE_SPECIFIQUE' ||
              (item.note && item.note.includes('[VISITE SPÉCIFIQUE')) ||
              (item.comment && item.comment.includes('[VISITE SPÉCIFIQUE'));

            const foundDelegate = delegatesListData.find(d => 
              (item.delegateId && d.id === item.delegateId) ||
              (item.delegateName && `${d.firstname} ${d.lastname}`.trim().toLowerCase() === item.delegateName.trim().toLowerCase())
            );
            const delegateCategory = item.category || item.delegateCategory || foundDelegate?.category;

            let displayNote = item.note || item.comment || '';
            if (displayNote.includes('Observations :')) {
              displayNote = displayNote.split('Observations :')[1].trim();
            }

            let cleanUnfinishedReason = '';
            if (isUnfinished) {
              cleanUnfinishedReason = displayNote
                .replace('[VISITE NON TERMINÉE]', '')
                .replace('[VISITE NON ABOUTIE]', '')
                .trim();
            }

            let itemBg = '#ffffff';
            let itemBorder = '1px solid #f0f0f0';
            let itemBorderLeft = '1px solid #f0f0f0';

            if (isUnfinished) {
              itemBg = '#fff7e6';
              itemBorder = '1px solid #ffd591';
              itemBorderLeft = '5px solid #fa541c';
            } else if (isSpecificVisit) {
              itemBg = '#f0f9ff';
              itemBorder = '1px solid #bae6fd';
              itemBorderLeft = '5px solid #0288d1';
            } else if (isSupervisor) {
              itemBg = '#fcf6ff';
              itemBorder = '1px solid #d3adf7';
              itemBorderLeft = '5px solid #722ed1';
            }

            return (
              <List.Item style={{ 
                padding: '12px 15px', 
                marginBottom: '8px',
                borderRadius: '6px',
                background: itemBg, 
                border: itemBorder,
                borderLeft: itemBorderLeft,
                transition: 'all 0.2s'
              }}>
                <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                  <div style={{ width: '15%' }}>
                    <Text strong style={{ color: isUnfinished ? '#d4380d' : 'inherit' }}>
                      {dayjs(item.reportDate).format('DD/MM/YYYY')}
                    </Text>
                    <br/>
                    <Text type="secondary" style={{ fontSize: '11px' }}>{dayjs(item.reportDate).format('HH:mm')}</Text>
                  </div>

                  <div style={{ width: '20%' }}>
                    <Space direction="vertical" size={2}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                        {isSupervisor ? (
                          <Tag color="purple" style={{ fontWeight: 'bold', padding: '2px 8px', borderRadius: '4px', margin: 0 }}>
                            <CrownOutlined style={{ marginRight: 5 }} /> {item.delegateName}
                          </Tag>
                        ) : (
                          <Tag icon={<UserOutlined />} color="default" style={{ margin: 0 }}>
                            {item.delegateName}
                          </Tag>
                        )}

                        {delegateCategory && (
                          <Tag 
                            color={getCategoryTagColor(delegateCategory)} 
                            style={{ fontWeight: 'bold', fontSize: '10px', padding: '1px 6px', borderRadius: '4px', margin: 0 }}
                          >
                            {delegateCategory}
                          </Tag>
                        )}
                      </div>

                      {isUnfinished && (
                        <Tag color="volcano" style={{ fontWeight: 'bold', fontSize: '10px', margin: 0 }}>
                          ⚠️ NON ABOUTIE
                        </Tag>
                      )}

                      {isSpecificVisit && (
                        <Tag color="blue" style={{ fontWeight: 'bold', fontSize: '10px', margin: 0 }}>
                          🎯 SPÉCIFIQUE
                        </Tag>
                      )}
                    </Space>
                  </div>

                  {/* ⚡ COLONNE ÉCHANTILLONS AVEC LE LABEL EN PRIORITÉ : */}
                  <div style={{ width: '16%' }}> 
                    {item.samples && item.samples.length > 0 ? (
                      item.samples.map((s, i) => {
                        // ⚡ Priorité au label (ex: "M-Memovit"), sinon fallback sur le nom complet
                        const displayName = s.label || s.name;

                        return (
                          <Tag 
                            key={i} 
                            color={s.isSpecific ? "error" : "processing"} 
                            style={{ marginBottom: '3px', fontSize: '11px', fontWeight: 500 }}
                          >
                            {s.isSpecific && "[HORS CIBLE] "}{displayName} x{s.quantity}
                          </Tag>
                        );
                      })
                    ) : (
                      <Text type="secondary" style={{ fontSize: '11px', fontStyle: isUnfinished ? 'italic' : 'normal', color: isUnfinished ? '#d4380d' : undefined }}>
                        {isUnfinished ? 'Aucun (Non aboutie)' : 'Aucun'}
                      </Text>
                    )}
                  </div>

                  <div style={{ width: '16%' }}> 
                    {item.products && item.products.length > 0 ? (
                      item.products.map((p, i) => (
                        <Tag key={i} color="orange" style={{ marginBottom: '3px', fontSize: '11px' }}>{p}</Tag>
                      ))
                    ) : (
                      <Text type="secondary" style={{ fontSize: '11px' }}>-</Text>
                    )}
                  </div>

                  <div style={{ width: '33%' }}>
                    {displayNote ? (
                      <Tooltip title={isUnfinished ? `Motif : ${cleanUnfinishedReason || displayNote}` : displayNote}>
                        <div style={{ 
                          fontSize: '12px', 
                          color: isUnfinished ? '#d4380d' : (isSupervisor ? '#531dab' : '#262626'), 
                          background: isUnfinished ? '#fff2e8' : (isSupervisor ? '#f9f0ff' : '#f5f5f5'),
                          padding: '5px 10px',
                          borderRadius: '4px',
                          border: isUnfinished ? '1px dashed #ffa39e' : (isSupervisor ? '1px dashed #d3adf7' : '1px solid #e8e8e8'),
                          maxHeight: '48px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          fontWeight: isUnfinished ? '600' : 'normal'
                        }}>
                          {isUnfinished ? `⚠️ Motif : ${cleanUnfinishedReason || displayNote}` : `📝 ${displayNote}`}
                        </div>
                      </Tooltip>
                    ) : (
                      <Text type="secondary" style={{ fontSize: '11px' }}>-</Text>
                    )}
                  </div>
                </div>
              </List.Item>
            );
          }} 
        />
        {doctorDetailData.length === 0 && !doctorLoading && <Empty description="Aucune visite trouvée pour ce médecin." />}
      </Spin>
    </Modal>
  );
};

export default DoctorHistoryModal;